import dynamic from 'next/dynamic'

import {
  SanityBlogAuthorPostGridModule,
  SanityBlogCategoryPostGridModule,
  SanityBlogPostGridModule,
  SanityBlogPostHeaderModule,
  SanityBlogPostBodyModule,
  SanityCaseOverviewModule,
  SanityDividerPhotoModule,
  SanityGridModule,
  SanityHeroModule,
  SanityHeroCaseVideoCarousel,
  SanityHeroVideoSliderModule,
  SanityMarqueeModule,
  SanityModule,
  SanityModuleType,
  SanityVideoModule,
  SanityLargeMediaTextModule,
} from '@data/sanity/queries/types/modules'
import { SanityBlogPostWithoutBody } from '@data/sanity/queries/types/blog'

const BlogPostGrid = dynamic(() => import('./blog/blog-post-grid'))
const BlogPostHeader = dynamic(() => import('./blog/blog-post-header'))
const BlogPostBody = dynamic(() => import('./blog/blog-post-body'))
const BlogPostReadMore = dynamic(() => import('./blog/blog-post-read-more'))
const CaseOverview = dynamic(() => import('./case-overview'))
const DividerPhoto = dynamic(() => import('./divider-photo'))
const LargeMediaText = dynamic(() => import('./large-media-text'))
const Grid = dynamic(() => import('./grid'))
const Hero = dynamic(() => import('./hero'))
const HeroCaseVideoCarousel = dynamic(
  () => import('./hero-case-video-carousel'),
)
const HeroVideoSlider = dynamic(() => import('./hero-video-slider'))
const Marquee = dynamic(() => import('./marquee'))
const VideoModule = dynamic(() => import('./video'))

interface ModuleProps {
  module: SanityModule
  posts?: SanityBlogPostWithoutBody[]
}

const Module = ({ module, posts }: ModuleProps) => {
  switch (module._type as SanityModuleType) {
    case SanityModuleType.GRID: {
      const gridModule = module as SanityGridModule

      return (
        <Grid
          columns={gridModule.columns}
          size={gridModule.size}
          spacing={gridModule.spacing}
          background={gridModule.background}
          fullWidth={gridModule.fullWidth}
          noColumnGaps={gridModule.noColumnGaps}
          noRowGaps={gridModule.noRowGaps}
          reverseSequence={gridModule.reverseSequence}
        />
      )
    }

    case SanityModuleType.HERO: {
      const heroModule = module as SanityHeroModule

      return (
        <Hero
          bgType={heroModule.bgType}
          contentPosition={heroModule.contentPosition}
          content={heroModule.content}
          showScrollIndicator={heroModule.showScrollIndicator}
          photos={heroModule.photos}
          muxVideo={heroModule.muxVideo}
          videoAspectRatio={heroModule.videoAspectRatio}
        />
      )
    }

    case SanityModuleType.HERO_CASE_VIDEO_CAROUSEL: {
      const heroCaseVideoCarouselModule = module as SanityHeroCaseVideoCarousel

      return <HeroCaseVideoCarousel items={heroCaseVideoCarouselModule.items} />
    }

    case SanityModuleType.HERO_VIDEO_SLIDER: {
      const heroVideoSliderModule = module as SanityHeroVideoSliderModule

      return <HeroVideoSlider slides={heroVideoSliderModule.slides} />
    }

    case SanityModuleType.DIVIDER_PHOTO: {
      const dividerPhotoModule = module as SanityDividerPhotoModule

      return (
        <DividerPhoto
          photo={dividerPhotoModule.photo}
          background={dividerPhotoModule.background}
        />
      )
    }

    case SanityModuleType.LARGE_MEDIA_TEXT: {
      const largeMediaTextModule = module as SanityLargeMediaTextModule

      return (
        <LargeMediaText
          mediaType={largeMediaTextModule.mediaType}
          image={largeMediaTextModule.image}
          video={largeMediaTextModule.video}
          content={largeMediaTextModule.content}
          frameColor={largeMediaTextModule.frameColor}
        />
      )
    }

    case SanityModuleType.CASE_OVERVIEW: {
      const caseOverviewModule = module as SanityCaseOverviewModule

      return (
        <CaseOverview
          title={caseOverviewModule.title}
          filterByTags={caseOverviewModule.filterByTags}
          cases={caseOverviewModule.cases}
        />
      )
    }

    case SanityModuleType.VIDEO: {
      const videoModule = module as SanityVideoModule

      return (
        <VideoModule
          type={videoModule.type}
          muxVideo={videoModule.muxVideo}
          settings={videoModule.settings}
          aspectRatio={videoModule.aspectRatio}
          borderRadius={videoModule.borderRadius}
          thumbnailImage={videoModule.thumbnailImage}
        />
      )
    }

    case SanityModuleType.MARQUEE: {
      const marqueeModule = module as SanityMarqueeModule

      return (
        <Marquee
          content={marqueeModule.content}
          contentAlignment={marqueeModule.contentAlignment}
          items={marqueeModule.items}
          speed={marqueeModule.speed ?? 0.5}
          reverse={marqueeModule.reverse}
          pausable={marqueeModule.pausable}
        />
      )
    }

    case SanityModuleType.BLOG_POST_GRID: {
      const blogPostGridModule = module as SanityBlogPostGridModule

      return (
        <BlogPostGrid
          posts={blogPostGridModule.posts}
          postsPerRow={blogPostGridModule.postsPerRow}
          options={blogPostGridModule.options}
        />
      )
    }

    case SanityModuleType.BLOG_POST_HEADER: {
      const blogPostHeaderModule = module as SanityBlogPostHeaderModule

      const { post, options } = blogPostHeaderModule

      if (!post) {
        return null
      }

      return <BlogPostHeader post={post} options={options} />
    }

    case SanityModuleType.BLOG_POST_BODY: {
      const blogPostBodyModule = module as SanityBlogPostBodyModule

      const { content } = blogPostBodyModule

      return <BlogPostBody content={content} />
    }

    case SanityModuleType.BLOG_POST_READ_MORE: {
      // const blogPostReadMoreModule = module as SanityBlogPostReadMoreModule

      return <BlogPostReadMore posts={posts} />
    }

    case SanityModuleType.BLOG_CATEGORY_POST_GRID: {
      const blogCategoryPostGridModule =
        module as SanityBlogCategoryPostGridModule

      return (
        <BlogPostGrid
          posts={posts ?? []}
          options={blogCategoryPostGridModule.options}
        />
      )
    }

    case SanityModuleType.BLOG_AUTHOR_POST_GRID: {
      const blogAuthorPostGridModule = module as SanityBlogAuthorPostGridModule

      return (
        <BlogPostGrid
          posts={posts ?? []}
          options={blogAuthorPostGridModule.options}
        />
      )
    }
  }
}

export default Module
